import React, { useState } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Check, X, ZoomIn } from 'lucide-react';
import { renderLatex } from '../lib/renderLatex';

// Image component with zoom functionality
const QuestionImage = ({ src, alt, className = "" }) => {
  const [zoomed, setZoomed] = useState(false);
  
  if (!src) return null;
  
  // Handle relative URLs from our backend
  const imageSrc = src.startsWith('/') ? `http://localhost:8000${src}` : src;
  
  return (
    <>
      <div className={`relative group cursor-pointer ${className}`} onClick={() => setZoomed(true)}>
        <img 
          src={imageSrc} 
          alt={alt || "Question image"} 
          className="max-w-full h-auto rounded-lg border border-border shadow-sm"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all rounded-lg flex items-center justify-center">
          <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
        </div>
      </div>
      
      {/* Zoom Modal */}
      {zoomed && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setZoomed(false)}
        >
          <img 
            src={imageSrc} 
            alt={alt || "Question image"} 
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
          />
          <button 
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70"
            onClick={() => setZoomed(false)}
          >
            <X size={24} />
          </button>
        </div>
      )}
    </>
  );
};

const QuestionCard = ({ question, selectedAnswer, onSelectAnswer, showAnswer = false, disabled = false }) => {
  // Check if this is a numerical question
  const isNumerical = question.question_type === 'numerical' || question.question_type === 'Numerical' || 
                      question.question_type === 'integer' || question.question_type === 'Integer' ||
                      (!question.options || question.options.length === 0);
  
  // Check if options have images
  const optionImages = question.option_images || {};
  
  // Check if user's answer is different from correct answer
  const userAnsweredWrong = showAnswer && selectedAnswer && selectedAnswer !== question.correct_answer;
  
  return (
    <div data-testid="question-card">
      <div className="mb-6">
        <div 
          className="text-lg md:text-xl font-medium text-foreground leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderLatex(question.question_text || '') }}
        />
        
        {/* Question Image */}
        {question.image_url && (
          <div className="mt-4">
            <QuestionImage src={question.image_url} alt="Question diagram" />
          </div>
        )}
      </div>

      {isNumerical ? (
        // Numerical Input
        <div className="space-y-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            Enter your answer (numerical value):
          </label>
          <Input
            type="number"
            step="any"
            value={selectedAnswer || ''}
            onChange={(e) => !disabled && onSelectAnswer && onSelectAnswer(e.target.value)}
            disabled={disabled}
            placeholder="Enter your answer"
            className="w-full max-w-md text-lg p-4 text-center font-mono text-foreground bg-background"
          />
          {showAnswer && (
            <div className={`mt-4 p-4 rounded-xl border-2 ${
              selectedAnswer === question.correct_answer
                ? 'bg-green-500/10 border-green-500'
                : 'bg-red-500/10 border-red-500'
            }`}>
              <p className="font-semibold text-foreground mb-1">
                {selectedAnswer === question.correct_answer ? '✓ Correct!' : '✗ Incorrect'}
              </p>
              <p className="text-sm text-foreground">
                Correct Answer: <span className="font-bold">{question.correct_answer}</span>
              </p>
            </div>
          )}
        </div>
      ) : (
        // MCQ Options
        <div className="grid grid-cols-1 gap-3">
          {question.options.map((option) => {
            const isSelected = selectedAnswer === option.label;
            const isCorrect = showAnswer && option.label === question.correct_answer;
            const isWrong = showAnswer && isSelected && option.label !== question.correct_answer;
            const isUserWrongChoice = showAnswer && isSelected && !isCorrect; // User selected this but it's wrong

            return (
              <button
                key={option.label}
                data-testid={`option-${option.label}`}
                onClick={() => !disabled && onSelectAnswer && onSelectAnswer(option.label)}
                disabled={disabled}
                className={`p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-start gap-3 ${
                  isCorrect
                    ? 'border-green-500 bg-green-500/10 text-green-700 dark:text-green-400'
                    : isUserWrongChoice
                    ? 'border-red-500 bg-red-500/10 text-red-700 dark:text-red-400'
                    : isSelected
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:border-primary/50 hover:bg-primary/5 text-foreground'
                } ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
                    isCorrect
                      ? 'border-green-500 bg-green-500'
                      : isUserWrongChoice
                      ? 'border-red-500 bg-red-500'
                      : isSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/50'
                  }`}
                >
                  {isCorrect && <Check size={14} className="text-white" />}
                  {isUserWrongChoice && <X size={14} className="text-white" />}
                  {isSelected && !showAnswer && <Check size={14} className="text-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">
                      <span className="mr-2 text-muted-foreground">{option.label}.</span>
                      <span dangerouslySetInnerHTML={{ __html: renderLatex(option.text || '') }} />
                    </span>
                    {showAnswer && isCorrect && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500 text-white text-xs font-semibold rounded-full">
                        <Check size={12} /> Correct Answer
                      </span>
                    )}
                    {showAnswer && isUserWrongChoice && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-xs font-semibold rounded-full">
                        <X size={12} /> Your Answer
                      </span>
                    )}
                  </div>
                  {/* Option Image */}
                  {optionImages[option.label] && (
                    <div className="mt-2">
                      <QuestionImage src={optionImages[option.label]} alt={`Option ${option.label}`} className="max-w-[200px]" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showAnswer && question.explanation && (
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl" data-testid="explanation">
          <p className="text-sm font-semibold text-primary mb-1">Explanation:</p>
          <p className="text-sm text-foreground whitespace-pre-line">{question.explanation}</p>
          {/* Solution Image */}
          {question.solution_image && (
            <div className="mt-3">
              <QuestionImage src={question.solution_image} alt="Solution diagram" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
